class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /*
   * Drawer-spezifisches updateQuantity (Stepper + / - / Remove).
   * Unterschied zur Basis (cart.js):
   *  - Bei Mengenaenderung mit verbleibenden Artikeln werden NUR die Items
   *    (.js-contents) und der Footer (.cart-drawer__footer) neu gerendert.
   *    Die Empfehlungen (<upsell-products>) liegen dazwischen und bleiben als
   *    Live-DOM-Knoten erhalten -> kein Refetch, kein Bild-Reload, Observer intakt.
   *  - Nur beim Leeren des Warenkorbs wird das vollstaendige .drawer__inner
   *    gerendert, damit der Empty-State (continue shopping / Login) erscheint.
   *  - Updates laufen ueber die Section Rendering API (?sections=cart-drawer),
   *    es gibt keinen vollen Cart-/Page-Reload.
   * Debounce (~300ms) kommt aus der Basisklasse (ON_CHANGE_DEBOUNCE_TIMER).
   */
  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const isEmpty = parsedState.item_count === 0;
        this.classList.toggle('is-empty', isEmpty);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);

        const doc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');

        if (isEmpty) {
          // Leerer Warenkorb: volles .drawer__inner rendern (Empty-State-Markup).
          const fresh = doc.querySelector('#CartDrawer .drawer__inner');
          const current = document.querySelector('#CartDrawer .drawer__inner');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        } else {
          // Artikel bleiben: nur Items + Footer ersetzen, Empfehlungen bleiben stehen.
          ['.js-contents', '.cart-drawer__footer'].forEach((selector) => {
            const target = document.querySelector(`#CartDrawer ${selector}`);
            const source = doc.querySelector(`#CartDrawer ${selector}`);
            if (target && source) target.innerHTML = source.innerHTML;
          });
        }

        // Verstecktes #cart-icon-bubble (Quelle des Header-Badges) aktualisieren.
        const bubble = document.getElementById('cart-icon-bubble');
        const bubbleHTML = parsedState.sections['cart-icon-bubble'];
        if (bubble && bubbleHTML) {
          const bubbleSrc = new DOMParser().parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section');
          if (bubbleSrc) bubble.innerHTML = bubbleSrc.innerHTML;
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (
          items.length === parsedState.items.length &&
          quantityElement &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          message =
            typeof updatedValue === 'undefined'
              ? window.cartStrings.error
              : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`) && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`));
        } else if (isEmpty && cartDrawerWrapper) {
          const emptyInner = cartDrawerWrapper.querySelector('.drawer__inner-empty');
          const firstLink = cartDrawerWrapper.querySelector('a');
          if (emptyInner && firstLink) trapFocus(emptyInner, firstLink);
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


if (!customElements.get("upsell-products")) {
  class UpsellProducts extends HTMLElement {
    constructor() {
      super();

      this.upsellList = this.querySelector('.upsell-products__list');
      this.switchButtons = this.querySelectorAll("[data-switch-action]")
      this.index;

      this.setupIntersectionObserver();
      this.setupSwitchButtons()

    }

    setupSwitchButtons() {
      this.switchButtons.forEach((btn, index) => {
        btn.addEventListener('click', () => {
          this.switchingCards(index)
        })
      });

    };

    switchingCards(index) {
      const scrollAmount = this.upsellList.children[0].clientWidth;
      this.upsellList.scrollLeft += (index === 0 ? -scrollAmount : scrollAmount);
    }

    setupIntersectionObserver() {
      const upsellObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.index = parseInt(entry.target.dataset.index);
              this.setActiveButton();
            }
          });
        },
        {
          threshold: 0.85,
        }
      );

      [...this.upsellList.children].forEach((card) => {
        upsellObserver.observe(card);
      });

    }
    setActiveButton() {
      let size = this.upsellList.children.length;
      this.index === 0
        ? (this.switchButtons[0].setAttribute('data-active', 'false'))
        : (this.switchButtons[0].setAttribute('data-active', 'true'));
      this.index === size - 1
        ? (this.switchButtons[1].setAttribute('data-active', 'false'))
        : (this.switchButtons[1].setAttribute('data-active', 'true'));
    }
  }

  customElements.define("upsell-products", UpsellProducts);
}


if (!customElements.get("upsell-button")) {
  class UpsellButton extends HTMLElement {
    constructor() {
      super();
      this.upsellProducts = this.closest("upsell-products");
      this.addEventListener('click', async (event) => {
        event.preventDefault();
        await this.addToCart(this.id, 1);
      });
    }

    async addToCart(variantId, quantity) {
      const res = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity
            }
          ]
        })
      });
      await this.render();
      return res;
    }

    async render() {
      const newHTML = await this.fetchSection();
      const parser = new DOMParser();
      let resAsHtml = parser.parseFromString(newHTML, 'text/html');
      const updatedCart = resAsHtml.querySelector('.cart-drawer');
      const currentContent = document.querySelector('.drawer__inner');
      const newContent = updatedCart.querySelector('.drawer__inner');
      document.querySelector('cart-drawer').classList.remove('is-empty');
      if (currentContent.innerHTML === newContent.innerHTML) return;
      currentContent.innerHTML = newContent.innerHTML;
      this.upsellProducts.setupSwitchButtons();
    }

    async fetchSection() {
      const res = await fetch(
        window.Shopify.routes.root + '?section_id=cart-drawer'
      );
      const text = await res.text();
      return text;
    }
  }

  customElements.define("upsell-button", UpsellButton);
}